import { inject, observer } from 'mobx-react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { runInAction } from 'mobx'
import { useHistory } from 'react-router-dom'
import {
  Button,
  Col,
  Empty,
  Form,
  Input,
  Row,
  Select,
  Space,
  Tag,
  message,
  DatePicker,
  Typography,
  Divider,
  Pagination,
} from 'antd'
import { Tooltip } from 'chart.js'
import {
  BankOutlined,
  FileExcelOutlined,
  PlusOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { FormButtonGroup, SelectList } from './FeedbackBoardPageStyled'
import ContentBlockWrapper from '../../../components/ContentBlockWrapper'
import PageTitle from '../../../components/PageTitle'
import DashboardLayout from '../../../layouts/DashboardLayout'
import TableComponent from '../../../components/Common/TableComponent'
import FeedbackBoardCreate from './FeedbackBoardCreate'
import { FeedbackProcedureRequest } from '../../../requests/FeedbackProcedureRequest'
import { TableBottomPaginationBlock } from '../../../components/Common/Table'
import { debounce } from 'lodash'
import AccessControlAction from '../../../components/AccessControlAction'
import { ACL_ACTION_TYPE } from '../../../constants'
import { ExportExcel } from './ExportExcel/ExportExcel'
import { systemName } from '../../../config'


const { RangePicker } = DatePicker
const { Title } = Typography

const FeedbackBoardPage = props => {
  const {
    selectPeopleStore,
    documentAccessibilityStore,
    authenticationStore,
    loadingAnimationStore,
    FeedbackProcedureStore,
    history,
  } = props
  const { departmentWithUserListV2 } = selectPeopleStore
  const {
    isVisibleSelectUserDepartmentPopup,
    setIsVisibleSelectUserDepartmentPopup,
    handleOpenSelectUserDepartmentPopup,
    handleCancelSelectUserDepartment,
    selectUserData,
    selectDepartmentData,
    selectGroupData,
  } = selectPeopleStore
  const [form] = Form.useForm()
  const [openFeedbackHDQT, setOpenFeedbackHDQT] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [dateRange, setDateRange] = useState(null)
  const [deptDataLoaded, setDeptDataLoaded] = useState(false)
  const {
    totalCount,
    feedbackData,
    commentData,
    page,
    size,
    setQueryParamsSignDocument,
    queryParamsSignDocument,
  } = FeedbackProcedureStore
  const searchTimeoutRef = useRef(null)

  const fetchFeedback = async () => {
    loadingAnimationStore.setTableLoading(true)
    try {
      setQueryParamsSignDocument({
        title: searchText,
        startDate: dateRange ? dateRange[0].format('DD-MM-YYYY') : null,
        endDate: dateRange ? dateRange[1].format('DD-MM-YYYY') : null,
        page: page,
        size: size,
      })

      await FeedbackProcedureStore.getFeedbackProcedureList(
        queryParamsSignDocument
      )
    } finally {
      loadingAnimationStore.setTableLoading(false)
    }
  }

  const result = feedbackData.map(item => ({
    ...item,
    feedbackComments: item.feedbackComments.map(comment => ({
      ...comment,
      feedbackCommentDetails: Object.values(
        comment.feedbackCommentDetails
          .filter(detail => detail.voteType !== null)
          .reduce((acc, detail) => {
            const { feedbackUsername, createdAt } = detail
            if (
              !acc[feedbackUsername] ||
              new Date(acc[feedbackUsername].createdAt) < new Date(createdAt)
            ) {
              acc[feedbackUsername] = detail
            }
            return acc
          }, {})
      ),
    })),
  }))

  const newFeedbackData = useMemo(() => {
    // Nếu chưa load xong danh sách phòng ban → trả về mảng rỗng → table loading hoặc empty
    if (!deptDataLoaded || !feedbackData) {
      return []
    }

    return feedbackData.map(item => {
      // === Tính vote count từ comment (giữ nguyên logic tốt của bạn) ===
      const voteCounts = item.feedbackComments.reduce(
        (counts, comment) => {
          comment.feedbackCommentDetails.forEach(detail => {
            if (detail.voteType && detail.voteType in counts) {
              counts[detail.voteType] += 1
            }
          })
          return counts
        },
        { AGREE: 0, DISAGREE: 0, OTHER: 0 }
      )

      // === Tính totalPeopleCountv2 dựa trên PIC + phòng ban (chỉ chạy khi dept data đã có) ===
      const picAssignees =
        item.assignees?.filter(a => a.permission === 'PIC') || []
      const uniqueAssignees = []
      const seenCodes = new Set()

      picAssignees.forEach(assignee => {
        const code = assignee.assignee_code
        if (!seenCodes.has(code)) {
          seenCodes.add(code)

          if (assignee.assignee_type === 'DPT') {
            const dept = selectPeopleStore.departmentWithUserListV2?.find(
              d => d.code === code
            )
            assignee.length = dept?.users?.length || 0
          }
          uniqueAssignees.push(assignee)
        } else {
          // Trường hợp trùng code: ưu tiên DPT hơn USER
          const existingIdx = uniqueAssignees.findIndex(
            a => a.assignee_code === code
          )
          if (
            existingIdx !== -1 &&
            uniqueAssignees[existingIdx].assignee_type === 'USER' &&
            assignee.assignee_type === 'DPT'
          ) {
            const dept = selectPeopleStore.departmentWithUserListV2?.find(
              d => d.code === code
            )
            assignee.length = dept?.users?.length || 0
            uniqueAssignees[existingIdx] = assignee
          }
        }
      })

      const totalPeopleCountv2 = uniqueAssignees.reduce((sum, a) => {
        return sum + (a.assignee_type === 'USER' ? 1 : a.length || 0)
      }, 0)

      const calcPercent = count =>
        totalPeopleCountv2 > 0
          ? Number(((count / totalPeopleCountv2) * 100).toFixed(2))
          : 0

      return {
        ...item,
        ...voteCounts,
        totalPeopleCountv2,
        agreePercentage: calcPercent(voteCounts.AGREE),
        disagreePercentage: calcPercent(voteCounts.DISAGREE),
        otherPercentage: calcPercent(voteCounts.OTHER),
      }
    })
  }, [feedbackData, selectPeopleStore.departmentWithUserListV2, deptDataLoaded])

  const handleOpenCreateFeedback = () => {
    setOpenFeedbackHDQT(true)
  }

  const handleCloseCreateFeedback = () => {
    setOpenFeedbackHDQT(false)
  }

  const debouncedFetchFeedback = useMemo(() => debounce(fetchFeedback, 300), [
    searchText,
    dateRange,
  ])

  useEffect(() => {
    debouncedFetchFeedback()
  }, [searchText, dateRange])

  useEffect(() => {
    const loadDepartmentData = async () => {
      if (
        !selectPeopleStore.departmentWithUserListV2 ||
        selectPeopleStore.departmentWithUserListV2.length === 0
      ) {
        try {
          loadingAnimationStore.showSpinner(true)
          // Giả sử store có action này (hầu hết các dự án đều có)
          await selectPeopleStore.getDepartmentWithUsersV2()
        } catch (error) {
          message.error('Không tải được danh sách phòng ban')
          console.error(error)
        } finally {
          loadingAnimationStore.showSpinner(false)
        }
      }
      setDeptDataLoaded(true)
    }

    loadDepartmentData()
  }, [selectPeopleStore])

  const handleSearchChange = e => {
    setSearchText(e.target.value)
  }

  const handleDateRangeChange = dates => {
    setDateRange(dates)
  }

  const exportFeedbackProcedure = async () => {
    try {
      runInAction(() => {
        FeedbackProcedureStore.page = 0
        FeedbackProcedureStore.size = 999999
        FeedbackProcedureStore.queryParamsSignDocument.page = 0
        FeedbackProcedureStore.queryParamsSignDocument.size = 999999
      })
      FeedbackProcedureStore.getFeedbackProcedureList()
      await ExportExcel(FeedbackProcedureStore.feedbackData)
    } catch (error) {
      console.log(error)
    } finally {
      runInAction(() => {
        FeedbackProcedureStore.page = page
        FeedbackProcedureStore.size = size
        FeedbackProcedureStore.queryParamsSignDocument.page = page
        FeedbackProcedureStore.queryParamsSignDocument.size = page
      })
    }
  }

  const onChangePage = async (page, pageSize) => {
    runInAction(() => {
      FeedbackProcedureStore.page = page - 1
      FeedbackProcedureStore.size = pageSize
      FeedbackProcedureStore.queryParamsSignDocument.page = page - 1
      FeedbackProcedureStore.queryParamsSignDocument.size = pageSize
    })

    loadingAnimationStore.setTableLoading(true)

    try {
      await FeedbackProcedureStore.getFeedbackProcedureList()
    } finally {
      loadingAnimationStore.setTableLoading(false)
    }
  }

  // Helper function để tính % cho từng comment
  const calculateCommentPercentages = (record, comment) => {
    const picAssignees =
      record.assignees?.filter(a => a.permission === 'PIC') || []
    const uniqueAssignees = []
    const seenCodes = new Set()

    for (const assignee of picAssignees) {
      if (!seenCodes.has(assignee.assignee_code)) {
        const matchingDepartment = departmentWithUserListV2?.find(
          dept => dept.code === assignee.assignee_code
        )
        if (matchingDepartment && assignee.assignee_type === 'DPT') {
          assignee.length = matchingDepartment.users?.length || 0
        }
        uniqueAssignees.push(assignee)
        seenCodes.add(assignee.assignee_code)
      } else {
        const existingIndex = uniqueAssignees.findIndex(
          a => a.assignee_code === assignee.assignee_code
        )
        if (
          existingIndex !== -1 &&
          uniqueAssignees[existingIndex].assignee_type === 'USER' &&
          assignee.assignee_type === 'DPT'
        ) {
          const matchingDepartment = departmentWithUserListV2?.find(
            dept => dept.code === assignee.assignee_code
          )
          if (matchingDepartment) {
            assignee.length = matchingDepartment.users?.length || 0
          }
          uniqueAssignees[existingIndex] = assignee
        }
      }
    }

    const totalPeopleCount = uniqueAssignees.reduce(
      (sum, assignee) =>
        sum + (assignee.assignee_type === 'USER' ? 1 : assignee.length || 0),
      0
    )

    const voteCounts = comment.feedbackCommentDetails.reduce(
      (counts, detail) => {
        if (detail.voteType in counts) {
          counts[detail.voteType] += 1
        }
        return counts
      },
      { AGREE: 0, DISAGREE: 0, OTHER: 0 }
    )

    return {
      agreePercent:
        totalPeopleCount > 0
          ? Number(((voteCounts.AGREE / totalPeopleCount) * 100).toFixed(2))
          : 0,
      disagreePercent:
        totalPeopleCount > 0
          ? Number(((voteCounts.DISAGREE / totalPeopleCount) * 100).toFixed(2))
          : 0,
      otherPercent:
        totalPeopleCount > 0
          ? Number(((voteCounts.OTHER / totalPeopleCount) * 100).toFixed(2))
          : 0,
    }
  }

  const tableColumns = useMemo(
    () => [
      {
        title: 'STT',
        width: 20,
        render: (text, record, index) =>
          FeedbackProcedureStore.page * FeedbackProcedureStore.size + index + 1,
      },
      {
        title: 'Nội dung ý kiến',
        width: 400,
        render: (text, record, index) => {
          // Lấy text thuần từ HTML
          const getPlainText = html => {
            const div = document.createElement('div')
            div.innerHTML = html
            return div.textContent || div.innerText || ''
          }

          return (
            <div>
              <div
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  marginBottom: '8px',
                  fontWeight: 600,
                }}
                dangerouslySetInnerHTML={{
                  __html: record.title,
                }}></div>

              {/* Hiển thị các nội dung ý kiến */}
              {record.feedbackComments?.map((comment, idx) => {
                if (!comment.content) return null

                const plainText = getPlainText(comment.content)
                const truncatedText =
                  plainText.length > 100
                    ? plainText.substring(0, 100) + '...'
                    : plainText

                return (
                  <div
                    key={idx}
                    style={{
                      marginBottom: '6px',
                      paddingLeft: '12px',
                    }}>
                    <div
                      title={plainText}
                      style={{
                        fontSize: '13px',
                      }}>
                      {'- ' + truncatedText}
                    </div>
                  </div>
                )
              })}
            </div>
          )
        },
      },
      {
        title: 'Ngày bắt đầu',
        width: 100,
        align: 'center',
        render: record => {
          const [day, month, year] = record?.startDate.split('-').map(Number)
          const date = new Date(year, month - 1, day)

          const formattedDay = String(date.getDate()).padStart(2, '0')
          const formattedMonth = String(date.getMonth() + 1).padStart(2, '0')
          const formattedYear = date.getFullYear()

          const formattedDate = `${formattedDay}/${formattedMonth}/${formattedYear}`

          return <p style={{ marginBottom: '0px' }}>{formattedDate}</p>
        },
      },
      {
        title: 'Kết quả',
        width: 100,
        render: (text, record) => {
          if (record.feedbackComments?.length >= 2) {
            return (
              <div>
                {record.feedbackComments.map((comment, idx) => {
                  if (!comment.content) return null
                  const {
                    agreePercent,
                    disagreePercent,
                    otherPercent,
                  } = calculateCommentPercentages(record, comment)

                  const result =
                    agreePercent > disagreePercent &&
                    agreePercent > otherPercent
                      ? 'Thông qua'
                      : 'Chưa thông qua'

                  return (
                    <p
                      key={idx}
                      style={{
                        marginBottom: '0px',
                        minHeight: '32px',
                        display: 'flex',
                        alignItems: 'center',
                      }}>
                      {result}
                    </p>
                  )
                })}
              </div>
            )
          }
          return (
            <p
              style={{
                marginBottom: '0px',
                minHeight: '32px',
                display: 'flex',
                alignItems: 'center',
              }}>
              {record?.agreePercentage > record?.disagreePercentage &&
              record?.agreePercentage > record?.otherPercentage
                ? 'Thông qua'
                : 'Chưa thông qua'}
            </p>
          )
        },
      },
      {
        title: 'Đồng ý',
        align: 'center',
        width: 100,
        render: (text, record) => {
          if (record.feedbackComments?.length >= 2) {
            return (
              <div>
                {record.feedbackComments.map((comment, idx) => {
                  if (!comment.content) return null
                  const { agreePercent } = calculateCommentPercentages(
                    record,
                    comment
                  )
                  return (
                    <p
                      key={idx}
                      style={{
                        marginBottom: '0px',
                        minHeight: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                      {agreePercent.toFixed(2)}%
                    </p>
                  )
                })}
              </div>
            )
          }
          return (
            <p style={{ marginBottom: '0px' }}>
              {record?.agreePercentage?.toFixed(2)}%
            </p>
          )
        },
      },
      {
        title: 'Không đồng ý',
        align: 'center',
        width: 100,
        render: (text, record) => {
          if (record.feedbackComments?.length >= 2) {
            return (
              <div>
                {record.feedbackComments.map((comment, idx) => {
                  if (!comment.content) return null
                  const { disagreePercent } = calculateCommentPercentages(
                    record,
                    comment
                  )
                  return (
                    <p
                      key={idx}
                      style={{
                        marginBottom: '0px',
                        minHeight: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                      {disagreePercent.toFixed(2)}%
                    </p>
                  )
                })}
              </div>
            )
          }
          return (
            <p style={{ marginBottom: '0px' }}>
              {record?.disagreePercentage?.toFixed(2)}%
            </p>
          )
        },
      },
      {
        title: 'Không có ý kiến/ Ý kiến khác',
        align: 'center',
        width: 100,
        render: (text, record) => {
          if (record.feedbackComments?.length >= 2) {
            return (
              <div>
                {record.feedbackComments.map((comment, idx) => {
                  if (!comment.content) return null
                  const { otherPercent } = calculateCommentPercentages(
                    record,
                    comment
                  )
                  return (
                    <p
                      key={idx}
                      style={{
                        marginBottom: '0px',
                        minHeight: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                      {otherPercent.toFixed(2)}%
                    </p>
                  )
                })}
              </div>
            )
          }
          return (
            <p style={{ marginBottom: '0px' }}>
              {record?.otherPercentage?.toFixed(2)}%
            </p>
          )
        },
      },
    ],
    [departmentWithUserListV2]
  )

  return (
    <>
      <DashboardLayout>
        <Helmet>
          <title>Phiếu lấy ý kiến | {systemName} Portal</title>
        </Helmet>
        <PageTitle location={props.location}>
          <div>
            <AccessControlAction aclActionType={ACL_ACTION_TYPE.create__HDQT}>
              <Button
                onClick={handleOpenCreateFeedback}
                type={'primary'}
                icon={<PlusOutlined />}>
                Tạo mới
              </Button>
            </AccessControlAction>
          </div>
        </PageTitle>
        <ContentBlockWrapper>
          <Row gutter={24 || 24}>
            <Col span={6}>
              <Form.Item
                style={{ marginBottom: 0, paddingBottom: 0 }}
                name={'search'}
                rules={[{ required: false }]}>
                <Input
                  allowClear
                  placeholder={'Tìm kiếm theo nội dung'}
                  value={searchText}
                  onChange={handleSearchChange}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                style={{ marginBottom: 0, paddingBottom: 0 }}
                name={'dateRange'}
                rules={[{ required: false }]}>
                <RangePicker
                  format={'DD/MM/YYYY'}
                  style={{ width: '100%' }}
                  value={dateRange}
                  onChange={handleDateRangeChange}
                />
              </Form.Item>
            </Col>
          </Row>
          <TableComponent
            rowKey={record => record.code}
            onRow={record => {
              return {
                onClick: () => {
                  history.push(
                    `/feedback-hdqt/feedback-detail/view/${record.code}`
                  )
                },
              }
            }}
            dataSource={newFeedbackData}
            columns={tableColumns}
            pagination={false}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={'Không có dữ liệu'}
                />
              ),
            }}
            loading={{
              tip: 'Đang tải phiếu ý kiến...',
              spinning: loadingAnimationStore.tableLoading,
            }}
          />
          <TableBottomPaginationBlock>
            <Pagination
              current={page + 1}
              total={totalCount}
              pageSize={size}
              hideOnSinglePage={true}
              onChange={onChangePage}
            />
          </TableBottomPaginationBlock>
        </ContentBlockWrapper>
      </DashboardLayout>
      {openFeedbackHDQT && (
        <FeedbackBoardCreate
          isVisible={openFeedbackHDQT}
          setIsVisible={setOpenFeedbackHDQT}
          handleCancel={handleCloseCreateFeedback}
          fetchFeedback={fetchFeedback}
        />
      )}
    </>
  )
}

export default inject(
  'selectUserStore',
  'documentAccessibilityStore',
  'selectPeopleStore',
  'loadingAnimationStore',
  'authenticationStore',
  'FeedbackProcedureStore'
)(observer(FeedbackBoardPage))
